<template>
  <div class="section-container">
    <div class="section-header">
      <h1 class="section-title">課程諮詢記錄</h1>
      <div class="table-controls">
        <div class="search-add-group">
          <input
            type="text"
            v-model="filterText"
            placeholder="搜索諮詢記錄..."
            class="search-input"
          />
          <button 
            class="button button-primary"
            @click="openModal(null)"
          >
            新增諮詢記錄
          </button>
        </div>  
      </div>
    </div>

    <div class="table-container">
      <div v-if="loading" class="empty-state">載入中...</div>
      <div v-else-if="error" class="empty-state">載入出錯: {{ error.message }}</div>
      <div v-else-if="!sortedAndFilteredInquiries.length" class="empty-state">暫無諮詢記錄</div>
      
      <table v-else class="data-table">
        <thead>
          <tr>
            <th @click="sortBy('created_at')">
              填表時間
              <span v-if="sortColumn === 'created_at'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sortBy('student_name')">
              學生姓名
              <span v-if="sortColumn === 'student_name'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sortBy('course_title')">
              諮詢課程
              <span v-if="sortColumn === 'course_title'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sortBy('school')">
              學校
              <span v-if="sortColumn === 'school'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sortBy('grade')">
              年級
              <span v-if="sortColumn === 'grade'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sortBy('parent_name')">
              家長姓名
              <span v-if="sortColumn === 'parent_name'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sortBy('status')">
              狀態
              <span v-if="sortColumn === 'status'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="inquiry in sortedAndFilteredInquiries" 
            :key="inquiry.id"
            class="clickable-row"
          >
            <td>{{ formatDate(inquiry.created_at) }}</td>
            <td>{{ inquiry.student_name }}</td>
            <td>{{ inquiry.course_title }}</td>
            <td>{{ inquiry.school }}</td>
            <td>{{ inquiry.grade }}</td>
            <td>{{ inquiry.parent_name }}</td>
            <td>
              <span :class="`status-badge ${inquiry.status.toLowerCase().replace('_', '-')}`">
                {{ statusLabels[inquiry.status] || inquiry.status }}
              </span>
            </td>
            <td class="actions">
              <button 
                @click="openModal(inquiry)" 
                class="button button-outline"
              >
                編輯
              </button>
              <button 
                @click="confirmDelete(inquiry.id)" 
                class="button button-outline delete-btn"
              >
                刪除
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <CourseInquiryModal
      v-if="showModal"
      :inquiry="currentInquiry"
      :isEdit="isEditMode"
      @close="closeModal"
      @saved="handleSaved"
    />
  </div>
</template>

<script setup>
import { useQuery, useMutation } from '@vue/apollo-composable'
import { GET_COURSE_INQUIRIES, DELETE_COURSE_INQUIRY } from '@/graphql/queries'
import { ref, computed } from 'vue'
import CourseInquiryModal from './CourseInquiryModal.vue'

// Status labels mapping
const statusLabels = {
  'NEW': '新諮詢',
  'IN_PROGRESS': '處理中',
  'RESOLVED_SALE': '已進班',
  'RESOLVED_NON_SALE': '未進班'
}

// Modal state
const showModal = ref(false)
const currentInquiry = ref(null)
const isEditMode = ref(false)

// Query data
const { result, loading, error, refetch } = useQuery(GET_COURSE_INQUIRIES)

// Sorting state
const sortColumn = ref('created_at')
const sortDirection = ref('desc')

// Filter state
const filterText = ref('')

// Computed properties
const courseInquiries = computed(() => result.value?.courseInquiries || [])

const sortedAndFilteredInquiries = computed(() => {
  let filtered = courseInquiries.value
  
  const searchText = filterText.value.trim().toLowerCase()
  if (searchText) {
    filtered = filtered.filter(inquiry =>
      inquiry.student_name.toLowerCase().includes(searchText) ||
      inquiry.course_title.toLowerCase().includes(searchText) ||
      inquiry.school.toLowerCase().includes(searchText) ||
      inquiry.grade.toLowerCase().includes(searchText) ||
      inquiry.parent_name.toLowerCase().includes(searchText) ||
      (statusLabels[inquiry.status] && statusLabels[inquiry.status].toLowerCase().includes(searchText)) ||
      formatDate(inquiry.created_at).toLowerCase().includes(searchText)
    )
  }
  
  return [...filtered].sort((a, b) => {
    let valA = a[sortColumn.value]
    let valB = b[sortColumn.value]
    
    if (sortColumn.value === 'created_at') {
      valA = new Date(valA).getTime()
      valB = new Date(valB).getTime()
    }
    
    if (sortColumn.value === 'status') {
      valA = statusLabels[valA] || valA
      valB = statusLabels[valB] || valB
    }
    
    if (typeof valA === 'string') {
      valA = valA.toLowerCase()
      valB = valB.toLowerCase()
    }
    
    if (valA < valB) {
      return sortDirection.value === 'asc' ? -1 : 1
    }
    if (valA > valB) {
      return sortDirection.value === 'asc' ? 1 : -1
    }
    return 0
  })
})

const { mutate: deleteInquiry } = useMutation(DELETE_COURSE_INQUIRY)

function openModal(inquiry) {
  currentInquiry.value = inquiry
  isEditMode.value = !!inquiry
  showModal.value = true
}

function closeModal() {
  showModal.value = false
}

function handleSaved() {
  refetch()
  closeModal()
}

function confirmDelete(id) {
  if (confirm('確定要刪除此諮詢記錄嗎？此操作不可撤銷。')) {
    deleteInquiry({ id }).then(() => {
      refetch()
    }).catch(err => {
      alert('刪除失敗: ' + err.message)
    })
  }
}

function formatDate(dateString) {
  const options = { 
    year: 'numeric', 
    month: '2-digit', 
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }
  return new Date(dateString).toLocaleString('zh-TW', options)
}

function sortBy(column) {
  if (sortColumn.value === column) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortColumn.value = column
    sortDirection.value = 'asc'
  }
}
</script>

<style scoped>
.table-container {
  overflow-x: auto;
  margin-top: 1rem;
}

.empty-state {
  padding: 2rem;
  text-align: center;
  color: var(--text-medium);
  background-color: var(--light-bg);
  border-radius: 4px;
  margin: 1rem 0;
}

.table-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.search-input {
  padding: 0.5rem 1rem;
  height: 32px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 0.9rem;
  width: 220px;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
}

.search-add-group {
  display: flex;
  align-items: center;
  gap: 0;
}

.search-add-group .search-input {
  border-radius: 4px 0 0 4px;
  border-right: none;
}

.search-add-group .button {
  border-radius: 0 4px 4px 0;
}

.actions {
  display: flex;
  gap: 8px;
  cursor: default;
}

.delete-btn {
  color: var(--danger-color);
  border-color: var(--danger-color);
}

.delete-btn:hover {
  background-color: rgba(244, 67, 54, 0.1);
}

.status-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  color: white;
  text-align: center;
  min-width: 70px;
}

.status-badge.new {
  background-color: #2196F3;
}

.status-badge.in-progress {
  background-color: #FF9800;
}

.status-badge.resolved-sale {
  background-color: #4CAF50;
}

.status-badge.resolved-non-sale {
  background-color: #9E9E9E;
}

th {
  cursor: pointer;
  position: relative;
  user-select: none;
}

th:hover {
  background-color: var(--light-bg);
}

.sort-icon {
  margin-left: 4px;
  font-size: 0.8em;
}

@media (max-width: 768px) {
  .table-controls {
    flex-direction: column;
    align-items: flex-end;
    gap: 0.75rem;
    width: 100%;
  }
  
  .search-add-group {
    width: 100%;
  }
  
  .search-add-group .search-input {
    flex-grow: 1;
    width: auto;
  }
  
  .actions {
    flex-direction: column;
    gap: 4px;
  }
  
  .button {
    width: 100%;
    text-align: center;
  }
}
</style>